import authService from '../services/authService';

/**
 * Pega Case Resolver Utility
 * 
 * Resolves a Pega case ID (pyID) to a full pzInsKey by:
 * 1. Fetching available case types from /casetypes API
 * 2. Finding common work class prefix across all case types
 * 3. Trying to resolve with the common prefix first
 * 4. If that fails, iterating through each case type to find a match
 * 
 * Usage:
 *   const resolver = getCaseResolver();
 *   await resolver.initialize(configId); // Fetches case types
 *   const result = resolver.resolveCase('HM-12345');
 */

class PegaCaseResolver {
  constructor() {
    this.configId = null;
    this.caseTypes = []; // Array of case type objects from API
    this.workClasses = []; // Extracted work class names (pxObjClass values)
    this.commonWorkClassPrefix = null; // Common prefix across all work classes
    this.isInitialized = false;
    this.initializationError = null;
  }

  /**
   * Initialize the resolver by fetching case types from Pega
   * @param {string} configId - The application/config ID
   * @returns {Promise<boolean>} - Whether initialization was successful
   */
  async initialize(configId) {
    if (this.isInitialized && this.configId === configId) {
      console.log('📋 PegaCaseResolver: Already initialized for this config');
      return true;
    }

    this.configId = configId;
    this.initializationError = null;

    try {
      console.log('📋 PegaCaseResolver: Fetching case types for config:', configId);
      
      const baseUrl = process.env.REACT_APP_PEGA_BASE_URL || process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${baseUrl}/api/v1/pega/prweb/api/application/v2/casetypes`, {
        method: 'GET',
        headers: {
          ...authService.getAuthHeader(),
          'Content-Type': 'application/json',
          'applicationId': configId,
          'channel': 'employee',
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch case types: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('📋 PegaCaseResolver: Case types response:', data);

      // Extract case types - handle both array and object with caseTypes property
      this.caseTypes = Array.isArray(data) ? data : (data.caseTypes || []);
      
      // Extract work classes (ID field typically contains the class name)
      this.workClasses = this.caseTypes
        .map(ct => ct.ID || ct.id || ct.pxObjClass || ct.pyClassName)
        .filter(Boolean);

      console.log('📋 PegaCaseResolver: Extracted work classes:', this.workClasses);

      // Find common prefix across all work classes
      this.commonWorkClassPrefix = this.findCommonPrefix(this.workClasses);
      console.log('📋 PegaCaseResolver: Common work class prefix:', this.commonWorkClassPrefix);
      
      // Derive and log intermediate work pools (these will be tried first when resolving cases)
      // e.g., ['Labb-LabbCS-Work-Service', 'Labb-LabbCS-Work-Infra', 'Labb-LabbCS-Work', 'Labb-LabbCS', 'Labb']
      const intermediateWorkPools = this.deriveIntermediateWorkPools();
      console.log('📋 PegaCaseResolver: Intermediate work pools (tried first):', intermediateWorkPools);

      this.isInitialized = true;
      return true;

    } catch (error) {
      console.error('❌ PegaCaseResolver: Initialization failed:', error);
      this.initializationError = error.message;
      this.isInitialized = false;
      return false;
    }
  }

  /**
   * Find the longest common prefix among an array of strings
   * For work classes like ['LABB-Work-Claim', 'LABB-Work-Service'], returns 'LABB-Work-'
   * @param {string[]} strings - Array of strings to find common prefix
   * @returns {string|null} - Common prefix or null if none found
   */
  findCommonPrefix(strings) {
    if (!strings || strings.length === 0) return null;
    if (strings.length === 1) return strings[0];

    // Sort to get the most different strings at the start and end
    const sorted = [...strings].sort();
    const first = sorted[0];
    const last = sorted[sorted.length - 1];

    let prefix = '';
    for (let i = 0; i < Math.min(first.length, last.length); i++) {
      if (first[i] === last[i]) {
        prefix += first[i];
      } else {
        break;
      }
    }

    // Ensure prefix ends at a meaningful boundary (hyphen or dash)
    // This avoids partial class names like "LABB-Work-Cla" vs "LABB-Work-"
    const lastHyphen = prefix.lastIndexOf('-');
    if (lastHyphen > 0 && lastHyphen < prefix.length - 1) {
      prefix = prefix.substring(0, lastHyphen + 1);
    }

    return prefix.length > 0 ? prefix : null;
  }

  /**
   * Derive the work pool class from the common prefix
   * For prefix like 'LABB-LABBCS-WORK-SERVICE-', returns 'LABB-LABBCS-WORK-SERVICE'
   * This is typically the base class that all case types inherit from
   * @param {string} prefix - Common prefix with trailing hyphen
   * @returns {string|null} - Work pool class name without trailing hyphen
   */
  deriveWorkPoolClass(prefix) {
    if (!prefix) return null;
    
    // Remove trailing hyphen if present
    let workPool = prefix.endsWith('-') ? prefix.slice(0, -1) : prefix;
    
    return workPool;
  }

  /**
   * Derive all intermediate work pool classes from the loaded case types
   * For example, from ['Labb-LabbCS-Work-Service-AddressChange', 'Labb-LabbCS-Work-Infra-Interaction']
   * Derives: ['Labb-LabbCS-Work-Service', 'Labb-LabbCS-Work-Infra', 'Labb-LabbCS-Work']
   * These are tried FIRST before iterating through specific case types
   * @returns {string[]} - Array of intermediate work pool classes (most specific first)
   */
  deriveIntermediateWorkPools() {
    if (!this.workClasses || this.workClasses.length === 0) return [];
    
    const intermediateClasses = new Set();
    
    // For each work class, extract potential intermediate pools
    // e.g., 'Labb-LabbCS-Work-Service-AddressChange' -> 'Labb-LabbCS-Work-Service', 'Labb-LabbCS-Work'
    for (const workClass of this.workClasses) {
      const parts = workClass.split('-');
      
      // Generate all possible prefixes by removing the last segment(s)
      // Skip the full class (last iteration) and empty prefix (first iteration with <2 parts)
      for (let i = parts.length - 1; i >= 2; i--) {
        const intermediateClass = parts.slice(0, i).join('-');
        intermediateClasses.add(intermediateClass);
      }
    }
    
    // Convert to array and sort by length (longest/most specific first)
    const sortedClasses = Array.from(intermediateClasses).sort((a, b) => b.length - a.length);
    
    console.log('📋 PegaCaseResolver: Derived intermediate work pools:', sortedClasses);
    return sortedClasses;
  }

  /**
   * Set the config ID (for lazy initialization)
   * @param {string} configId - The application/config ID
   */
  setConfigId(configId) {
    if (this.configId !== configId) {
      // Config changed, reset initialization
      this.configId = configId;
      this.isInitialized = false;
      this.caseTypes = [];
      this.workClasses = [];
      this.commonWorkClassPrefix = null;
    }
  }

  /**
   * Resolve a pyID to a full pzInsKey (uppercase)
   * @param {string} input - The case ID (e.g., "hm-12345") or full pzInsKey
   * @returns {{pzInsKey: string, pxObjClass: string, pyID: string, needsValidation: boolean, possibleClasses: string[]} | null}
   */
  resolveCase(input) {
    if (!input || typeof input !== 'string') {
      console.warn('⚠️ PegaCaseResolver: Invalid input provided');
      return null;
    }

    // Uppercase and trim the input
    const trimmedInput = input.trim().toUpperCase();

    // Check if it's already a full pzInsKey (contains space)
    if (trimmedInput.includes(' ')) {
      const [pxObjClass, ...idParts] = trimmedInput.split(' ');
      const pyID = idParts.join(' ');
      console.log('📋 PegaCaseResolver: Input is full pzInsKey', { pxObjClass, pyID });
      return {
        pzInsKey: trimmedInput,
        pxObjClass,
        pyID,
        needsValidation: false,
        possibleClasses: [pxObjClass]
      };
    }

    // Need to resolve using case types
    if (!this.isInitialized || this.workClasses.length === 0) {
      console.warn('⚠️ PegaCaseResolver: Not initialized or no case types available');
      return {
        pzInsKey: null,
        pxObjClass: null,
        pyID: trimmedInput,
        needsValidation: true,
        possibleClasses: [],
        error: this.initializationError || 'Case resolver not initialized. Please try again.'
      };
    }

    // Return all possible classes for validation
    const sortedClasses = [...this.workClasses].sort((a, b) => {
      // Prefer shorter class names (more generic)
      return a.length - b.length;
    });

    const firstClass = sortedClasses[0];
    const pzInsKey = `${firstClass} ${trimmedInput}`.toUpperCase();

    console.log('📋 PegaCaseResolver: Resolved case with possibilities:', { 
      pyID: trimmedInput, 
      firstClass, 
      pzInsKey,
      totalPossibilities: sortedClasses.length 
    });

    return {
      pzInsKey,
      pxObjClass: firstClass,
      pyID: trimmedInput,
      needsValidation: sortedClasses.length > 1, // Only need validation if multiple case types
      possibleClasses: sortedClasses
    };
  }

  /**
   * Validate a case exists via the /cases/{id} API
   * @param {string} pzInsKey - The full case key (e.g., "LABB-WORK-CASE HM-12345")
   * @returns {Promise<{valid: boolean, error?: string}>}
   */
  async validateCase(pzInsKey) {
    try {
      const baseUrl = process.env.REACT_APP_PEGA_BASE_URL || process.env.REACT_APP_API_BASE_URL;
      const encodedCaseId = encodeURIComponent(pzInsKey);

      const response = await fetch(`${baseUrl}/api/v1/pega/prweb/api/application/v2/cases/${encodedCaseId}`, {
        method: 'GET',
        headers: {
          ...authService.getAuthHeader(),
          'Content-Type': 'application/json',
          'applicationId': this.configId,
          'channel': 'employee',
        }
      });

      if (response.ok) {
        console.log('✅ PegaCaseResolver: Case validated successfully:', pzInsKey);
        return { valid: true };
      } else if (response.status === 404) {
        console.log('❌ PegaCaseResolver: Case not found:', pzInsKey);
        return { valid: false, error: 'Case not found' };
      } else if (response.status === 403) {
        console.log('� PegaCaseResolver: Access denied to case:', pzInsKey);
        return { valid: false, error: 'Access denied' };
      } else {
        const errorText = await response.text();
        console.log('❌ PegaCaseResolver: Case validation failed:', response.status, errorText);
        return { valid: false, error: `Error ${response.status}` };
      }
    } catch (error) {
      console.error('❌ PegaCaseResolver: Case validation error:', error);
      return { valid: false, error: error.message };
    }
  }

  /**
   * Find and validate the correct case key by trying each possible work class
   * Strategy:
   * 1. First try intermediate work pool classes (e.g., -Service, -Infra, base Work class)
   * 2. If that fails, iterate through each specific case type
   * 
   * @param {string} pyID - The case ID (e.g., "HM-12345")
   * @returns {Promise<{pzInsKey: string, pxObjClass: string, pyID: string} | {error: string}>}
   */
  async findValidCase(pyID) {
    const uppercasePyID = pyID.trim().toUpperCase();
    
    if (!this.isInitialized || this.workClasses.length === 0) {
      return { error: 'Case resolver not initialized. Please try again.' };
    }

    // Check if it's already a full pzInsKey (contains space)
    if (uppercasePyID.includes(' ')) {
      const validation = await this.validateCase(uppercasePyID);
      if (validation.valid) {
        const [pxObjClass, ...idParts] = uppercasePyID.split(' ');
        return {
          pzInsKey: uppercasePyID,
          pxObjClass,
          pyID: idParts.join(' ')
        };
      }
      return { error: validation.error || 'Case not found or access denied' };
    }

    // STRATEGY 1: Try intermediate work pool classes FIRST
    // These are derived from the case types (e.g., -Service, -Infra, base Work)
    // Most likely to contain the case since they're the common parent classes
    const intermediateWorkPools = this.deriveIntermediateWorkPools();
    
    if (intermediateWorkPools.length > 0) {
      console.log(`🔍 PegaCaseResolver: Trying ${intermediateWorkPools.length} intermediate work pools first for ${uppercasePyID}`);
      
      for (const workPool of intermediateWorkPools) {
        const pzInsKey = `${workPool.toUpperCase()} ${uppercasePyID}`;
        console.log(`🔍 PegaCaseResolver: Trying work pool: ${pzInsKey}`);
        
        const validation = await this.validateCase(pzInsKey);
        
        if (validation.valid) {
          console.log(`✅ PegaCaseResolver: Found valid case in work pool: ${pzInsKey}`);
          return {
            pzInsKey,
            pxObjClass: workPool.toUpperCase(),
            pyID: uppercasePyID
          };
        }
      }
      console.log(`ℹ️ PegaCaseResolver: Case not in any work pool, checking specific case types...`);
    }

    // STRATEGY 2: Try each specific case type
    const sortedClasses = [...this.workClasses].sort((a, b) => a.length - b.length);
    
    console.log(`🔍 PegaCaseResolver: Searching for case ${uppercasePyID} across ${sortedClasses.length} case types`);

    for (let i = 0; i < sortedClasses.length; i++) {
      const pxObjClass = sortedClasses[i].toUpperCase();
      const pzInsKey = `${pxObjClass} ${uppercasePyID}`;
      
      console.log(`🔍 PegaCaseResolver: Trying ${i + 1}/${sortedClasses.length}: ${pzInsKey}`);
      
      const validation = await this.validateCase(pzInsKey);
      
      if (validation.valid) {
        console.log(`✅ PegaCaseResolver: Found valid case: ${pzInsKey}`);
        return {
          pzInsKey,
          pxObjClass,
          pyID: uppercasePyID
        };
      }
    }

    // All possibilities exhausted
    console.log('❌ PegaCaseResolver: Case not found in any case type');
    return { error: 'Case not found or you do not have access' };
  }

  /**
   * Get all possible pzInsKeys for a pyID (for iteration/fallback)
   * @param {string} pyID - The case ID without class
   * @returns {Array<{pzInsKey: string, pxObjClass: string}>}
   */
  getAllPossibleKeys(pyID) {
    if (!this.isInitialized || this.workClasses.length === 0) {
      return [];
    }
    const uppercasePyID = pyID.toUpperCase();
    return this.workClasses.map(pxObjClass => ({
      pzInsKey: `${pxObjClass.toUpperCase()} ${uppercasePyID}`,
      pxObjClass: pxObjClass.toUpperCase()
    }));
  }

  /**
   * Clear cached data (call on logout or story change)
   */
  reset() {
    console.log('🗑️ PegaCaseResolver: Resetting');
    this.configId = null;
    this.caseTypes = [];
    this.workClasses = [];
    this.commonWorkClassPrefix = null;
    this.isInitialized = false;
    this.initializationError = null;
  }

  /**
   * Check if resolver is initialized with case types
   */
  get initialized() {
    return this.isInitialized && this.workClasses.length > 0;
  }

  /**
   * Get status information for debugging
   */
  getStatus() {
    return {
      initialized: this.isInitialized,
      configId: this.configId,
      caseTypeCount: this.caseTypes.length,
      workClasses: this.workClasses,
      commonPrefix: this.commonWorkClassPrefix,
      intermediateWorkPools: this.deriveIntermediateWorkPools(),
      error: this.initializationError
    };
  }
}

// Singleton instance for app-wide use
let resolverInstance = null;

/**
 * Get the singleton PegaCaseResolver instance
 */
export function getCaseResolver() {
  if (!resolverInstance) {
    resolverInstance = new PegaCaseResolver();
  }
  return resolverInstance;
}

/**
 * Reset the singleton instance (call on logout)
 */
export function resetCaseResolver() {
  if (resolverInstance) {
    resolverInstance.reset();
  }
}

export default PegaCaseResolver;
